import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    Box, 
    Typography,
    Button,
    Alert,
    Divider,
    Paper,
    useTheme,
    CircularProgress,
} from '@mui/material';
import { Google as GoogleIcon, Microsoft as MicrosoftIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { formInputStyles, formContainerStyles } from '../../styles/formStyles';
import { useToast } from '@/components/common/Toast';
import { api } from '@/services/api';
import { SSOService } from '@/services/auth/SSOService';
import LoginForm from '@/components/auth/LoginForm';
import type { TenantConfig, SSOConfig, LoginCredentials } from '@/services/api/domains/auth';

const LoginPage: React.FC = () => {
    const toast = useToast();
    const navigate = useNavigate();
    const theme = useTheme();
    const { login } = useAuth();
    const [tenantConfig, setTenantConfig] = useState<TenantConfig | null>(null);
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchTenantConfig = async () => {
            try {
                const response = await api.auth.getTenantConfig();
                setTenantConfig(response.data);
            } catch (error: any) {
                console.error('Failed to fetch tenant config:', error);
                setError(error.response?.data?.error?.message || 'Failed to load tenant configuration');
            } finally {
                setLoadingConfig(false);
            }
        };

        fetchTenantConfig();
    }, []);

    const handleSSOLogin = async (provider: 'google' | 'azure') => {
        if (!tenantConfig?.auth) return;

        try {
            const ssoConfig: SSOConfig = {
                provider,
                client_id: tenantConfig.auth.client_id!,
                metadata_url: tenantConfig.auth.metadata_url!,
                ...(tenantConfig.auth.tenant_id && { tenant_id: tenantConfig.auth.tenant_id })
            };

            const { token } = await SSOService.getToken(ssoConfig);
            
            const credentials: LoginCredentials = {
                sso_token: token,
                district_domain: tenantConfig.domain
            };

            await login(credentials);
        } catch (error: any) {
            console.error('SSO login failed:', error);
            toast.showError(error.message || 'SSO login failed');
        }
    };

    if (loadingConfig) {
        return (
            <Box sx={{ 
                minHeight: '100vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
            }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ 
                minHeight: '100vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                p: 3
            }}>
                <Alert severity="error" sx={{ maxWidth: 400 }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100vw',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: (theme) => theme.palette.grey[50],
                overflow: 'hidden',
                ...formInputStyles(theme),
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    width: '100%',
                    p: { xs: 2, sm: 3 },
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '440px',
                        mx: 'auto',
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={formContainerStyles(theme)}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                mb: 4,
                                cursor: 'pointer',
                            }}
                            onClick={() => navigate('/')}
                        >
                            <img 
                                src="/src/assets/logos/dreamzconnect.svg" 
                                alt="DreamsConnect Logo" 
                                style={{
                                    height: '45px',
                                    width: 'auto',
                                    marginBottom: '8px',
                                }}
                            />
                        </Box>

                        <Box sx={{ 
                            textAlign: 'center', 
                            mb: 3,
                        }}>
                            <Typography
                                component="h1"
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    color: 'primary.main',
                                    mb: 1,
                                    fontSize: { xs: '1.75rem', sm: '2.125rem' },
                                }}
                            >
                                Welcome to {tenantConfig?.name}
                            </Typography>
                            <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ px: { xs: 1, sm: 2 } }}
                            >
                                Sign in to continue to your dashboard
                            </Typography>
                        </Box>

                        {tenantConfig?.auth.sso_enabled && (
                            <Box sx={{ mb: 3 }}>
                                {tenantConfig.auth.provider === 'google' && (
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        fullWidth
                                        onClick={() => handleSSOLogin('google')}
                                        startIcon={<GoogleIcon />}
                                        sx={{
                                            borderColor: '#4285f4',
                                            color: '#4285f4',
                                            py: 1.5,
                                            '&:hover': {
                                                borderColor: '#2b6cd4',
                                                backgroundColor: 'rgba(66, 133, 244, 0.04)',
                                            },
                                        }}
                                    >
                                        Sign in with Google
                                    </Button>
                                )}

                                {tenantConfig.auth.provider === 'azure' && (
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        fullWidth
                                        onClick={() => handleSSOLogin('azure')}
                                        startIcon={<MicrosoftIcon />}
                                        sx={{
                                            borderColor: '#0078d4',
                                            color: '#0078d4',
                                            py: 1.5,
                                            '&:hover': {
                                                borderColor: '#005a9e',
                                                backgroundColor: 'rgba(0, 120, 212, 0.04)',
                                            },
                                        }}
                                    >
                                        Sign in with Microsoft
                                    </Button>
                                )}
                            </Box>
                        )}

                        {tenantConfig?.auth.allow_password_login && (
                            <>
                                {tenantConfig.auth.sso_enabled && (
                                    <Divider sx={{ my: 3 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            OR
                                        </Typography>
                                    </Divider>
                                )}
                                <LoginForm onSubmit={login} />
                            </>
                        )}

                        <Box sx={{ 
                            textAlign: 'center',
                            mt: 3
                        }}>
                            <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ lineHeight: 1.6 }}
                            >
                                Don't have an account?{' '}
                                <Link 
                                    to="/register"
                                    style={{
                                        color: '#1976d2',
                                        textDecoration: 'none',
                                        fontWeight: 600,
                                    }}
                                >
                                    Sign up here
                                </Link>
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};

export default LoginPage;